import {ApiError} from 'client';
import type {ContextEngineChatBody} from 'client/models/ContextEngineChatBody';
import {
  extractCeSourcesFromRaw,
  lastAssistantTextFromCeResponse,
} from 'helpers/contextEngineRawResponse';
import {getBackendUrl} from 'utilities';

import type {ClarificationWizardState, ParsedQuestion} from './clarificationQuestions';

/** Max https links sent as Context Engine attachments from this chat. */
export const MAX_WORKSTREAM_CONTEXT_ATTACHMENTS = 20;

export type ClarificationFreeformInput = {
  kind: 'freeform';
};

export type ClarificationSingleSelectOption = {
  id: string;
  label: string;
};

export type ClarificationSingleSelectInput = {
  kind: 'single_select';
  options: ClarificationSingleSelectOption[];
};

export type ClarificationQuestion = {
  id: string;
  prompt: string;
  input: ClarificationFreeformInput | ClarificationSingleSelectInput;
};

export type ClarificationPayload = {
  schema_version: '1';
  intro?: string;
  questions: ClarificationQuestion[];
};

export type WorkstreamContextEngineChatResponse = Record<string, unknown>;

export type WorkstreamContextEngineChatRequestBody = {
  message: string;
  conversation_id?: string;
  history?: Array<Record<string, unknown>>;
  draft_markdown?: string;
  selected_status?: string;
  draft_content_hash?: string;
  assist_guardrails?: ContextEngineChatBody['assist_guardrails'];
  /** Full attachment set for this chat; send [] to attach nothing. Omit for legacy server behavior. */
  user_attachments?: ContextEngineChatBody['user_attachments'] | null;
  previous_update_markdown?: string;
};

export function buildWorkstreamContextEngineChatPayload(
  body: WorkstreamContextEngineChatRequestBody
): ContextEngineChatBody {
  // OpenAPI currently says `history` is `null`, but the endpoint still accepts the
  // existing frame array shape used by the chat UI.
  return {
    message: body.message,
    conversation_id: body.conversation_id ?? '',
    history: body.history as unknown as ContextEngineChatBody['history'],
    draft_markdown: body.draft_markdown ?? '',
    selected_status: body.selected_status ?? '',
    draft_content_hash: body.draft_content_hash ?? '',
    assist_guardrails: body.assist_guardrails ?? null,
    ...(body.user_attachments !== undefined && body.user_attachments !== null
      ? {user_attachments: body.user_attachments}
      : {}),
    ...(body.previous_update_markdown != null && body.previous_update_markdown !== ''
      ? {previous_update_markdown: body.previous_update_markdown}
      : {}),
  };
}

type NdjsonStreamLine =
  | {type: 'frame'; payload: Record<string, unknown>}
  | {type: 'done'; result: WorkstreamContextEngineChatResponse}
  | {type: 'error'; message?: string; grpc_code?: string};

function isTerminalOrMetaProgressUpdate(o: Record<string, unknown>): boolean {
  if (o.terminal === true) {
    return true;
  }
  const message = typeof o.message === 'string' ? o.message.trim().toLowerCase() : '';
  return message === 'show thinking';
}

function parseProgressTimestamp(o: Record<string, unknown>): string | undefined {
  const ts = o.timestamp;
  if (ts === undefined || ts === null) {
    return undefined;
  }
  if (typeof ts === 'string') {
    const trimmed = ts.trim();
    return trimmed || undefined;
  }
  if (typeof ts === 'number' && Number.isFinite(ts)) {
    return String(ts);
  }
  return undefined;
}

export type ContextEngineProgressSearch = {
  query: string;
};

export type ContextEngineProgressStep = {
  step?: number;
  message: string;
  description?: string;
  timestamp?: string;
  searches?: ContextEngineProgressSearch[];
};

function parseProgressUpdateItem(item: unknown): ContextEngineProgressStep | null {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const o = item as Record<string, unknown>;
  if (isTerminalOrMetaProgressUpdate(o)) {
    return null;
  }
  const rawMessage = typeof o.message === 'string' ? o.message.trim() : '';
  const rawDesc = typeof o.description === 'string' ? o.description.trim() : '';
  const agentId = typeof o.agent_id === 'string' ? o.agent_id.trim() : '';
  const stepNum = typeof o.step === 'number' && Number.isFinite(o.step) ? o.step : undefined;

  let searches: ContextEngineProgressSearch[] | undefined;
  if (Array.isArray(o.searches)) {
    const list: ContextEngineProgressSearch[] = [];
    for (const s of o.searches) {
      if (s && typeof s === 'object') {
        const q =
          typeof (s as {query?: unknown}).query === 'string'
            ? (s as {query: string}).query.trim()
            : '';
        if (q) {
          list.push({query: q});
        }
      }
    }
    if (list.length) {
      searches = list;
    }
  }

  const title =
    rawMessage || rawDesc || agentId || (stepNum !== undefined ? `Step ${stepNum}` : '');
  if (!title && !searches?.length) {
    return null;
  }
  const message = title || (searches?.length ? searches[0]?.query?.slice(0, 72) || 'Search' : '');
  const description = rawMessage && rawDesc && rawDesc !== rawMessage ? rawDesc : undefined;
  const ts = parseProgressTimestamp(o);
  return {
    ...(stepNum !== undefined ? {step: stepNum} : {}),
    message,
    ...(description ? {description} : {}),
    ...(ts ? {timestamp: ts} : {}),
    ...(searches ? {searches} : {}),
  };
}

/**
 * Parsed `progress_updates` from a streamed CE frame (full list as sent by the server for that frame).
 * Omits terminal / meta rows (e.g. "show thinking").
 */
export function contextEngineStreamProgressStepsFromFrame(
  payload: Record<string, unknown>
): ContextEngineProgressStep[] {
  const pu = payload.progress_updates;
  if (!Array.isArray(pu) || pu.length === 0) {
    return [];
  }
  const out: ContextEngineProgressStep[] = [];
  for (const item of pu) {
    const parsed = parseProgressUpdateItem(item);
    if (parsed) {
      out.push(parsed);
    }
  }
  return out;
}

type NdjsonHandled = 'frame' | 'done' | 'ignored';

function handleNdjsonLine(
  trimmed: string,
  options: {onFrame?: (framePayload: Record<string, unknown>) => void} | undefined,
  onDone: (r: WorkstreamContextEngineChatResponse) => void
): NdjsonHandled {
  let parsed: NdjsonStreamLine;
  try {
    parsed = JSON.parse(trimmed) as NdjsonStreamLine;
  } catch {
    throw new Error('Invalid stream data from server');
  }
  if (parsed.type === 'frame' && parsed.payload && typeof parsed.payload === 'object') {
    options?.onFrame?.(parsed.payload);
    return 'frame';
  }
  if (parsed.type === 'done' && parsed.result && typeof parsed.result === 'object') {
    onDone(parsed.result);
    return 'done';
  }
  if (parsed.type === 'error') {
    throw new Error(parsed.message?.trim() || 'Context Engine stream error');
  }
  return 'ignored';
}

async function yieldToUiAfterFrame(): Promise<void> {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

/**
 * POST stream: NDJSON lines from ``/context-engine/chat/stream`` until ``done``.
 * ``result`` matches the Context Engine ``get_response`` JSON shape.
 */
export async function streamWorkstreamContextEngineChat(
  workstreamId: number,
  payload: ContextEngineChatBody,
  options?: {
    onFrame?: (framePayload: Record<string, unknown>) => void;
    signal?: AbortSignal;
  }
): Promise<WorkstreamContextEngineChatResponse> {
  const base = getBackendUrl().replace(/\/+$/, '');
  const url = `${base}/api/v1/workstream/${workstreamId}/context-engine/chat/stream`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
  if (!res.ok) {
    let detail = res.statusText || 'Request failed';
    try {
      const j: unknown = await res.json();
      if (j && typeof j === 'object' && 'detail' in j) {
        detail = String((j as {detail?: unknown}).detail);
      }
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  const body = res.body;
  if (!body) {
    throw new Error('Empty response body');
  }
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let result: WorkstreamContextEngineChatResponse | null = null;
  let streamAccumulator: Record<string, unknown> | null = null;
  const streamOptions = {
    ...options,
    onFrame: (framePayload: Record<string, unknown>) => {
      streamAccumulator = mergeCeStreamingAssistantPayload(streamAccumulator, framePayload);
      options?.onFrame?.(framePayload);
    },
  };
  const onStreamDone = (r: WorkstreamContextEngineChatResponse) => {
    const merged = mergeCeStreamingAssistantPayload(
      streamAccumulator,
      r as Record<string, unknown>
    );
    result = merged as WorkstreamContextEngineChatResponse;
  };
  let streamDone = false;
  while (!streamDone) {
    if (options?.signal?.aborted) {
      try {
        await reader.cancel();
      } catch {
        // ignore
      }
      throw new DOMException('The user aborted a request.', 'AbortError');
    }
    const {done, value} = await reader.read();
    streamDone = done;
    buf += value ? decoder.decode(value, {stream: !done}) : '';
    let nl = buf.indexOf('\n');
    while (nl >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (line) {
        const handled = handleNdjsonLine(line, streamOptions, onStreamDone);
        if (handled === 'frame') {
          await yieldToUiAfterFrame();
        }
      }
      nl = buf.indexOf('\n');
    }
  }
  const tail = buf.trim();
  if (tail) {
    const handled = handleNdjsonLine(tail, streamOptions, onStreamDone);
    if (handled === 'frame') {
      await yieldToUiAfterFrame();
    }
  }
  if (!result) {
    throw new Error('Stream ended without a result');
  }
  return result;
}

/** True when ``fetch`` / stream read was aborted via ``AbortSignal`` (user stopped generation). */
export function isContextEngineStreamAbortError(err: unknown): boolean {
  if (
    typeof DOMException !== 'undefined' &&
    err instanceof DOMException &&
    err.name === 'AbortError'
  ) {
    return true;
  }
  return err instanceof Error && err.name === 'AbortError';
}

/** Map poll/HTTP failures to a readable Error (same detail shape as other API errors). */
export function contextEngineChatErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.body;
    if (d && typeof d === 'object' && 'detail' in d) {
      return String((d as {detail?: unknown}).detail);
    }
    return err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}

/** If the model merged preamble + "1. …" into a single question prompt, split it out. */
function splitEmbeddedPreambleBeforeFirstNumberedLine(
  prompt: string
): {preamble: string; question: string} | null {
  const re = /^1\.\s+/m;
  const m = re.exec(prompt);
  if (!m || m.index === undefined) return null;
  if (m.index === 0) return null;
  const preamble = prompt.slice(0, m.index).trim();
  const after = prompt.slice(m.index + m[0].length).trim();
  if (preamble.length < 5 || !after) return null;
  return {preamble, question: after};
}

function stripLeadingIntroFromPrompt(intro: string, prompt: string): string {
  const i = intro.trim();
  const p = prompt.trim();
  if (!i) return p;
  if (p.startsWith(i)) {
    const rest = p.slice(i.length).trimStart();
    return rest || p;
  }
  const i2 = i.replace(/\r\n/g, '\n');
  const p2 = p.replace(/\r\n/g, '\n');
  if (p2.startsWith(i2)) {
    const rest = p2.slice(i2.length).trimStart();
    return rest || p;
  }
  return p;
}

/** Map API clarification payload to wizard UI state (same shape as before the hand-rolled parser). */
export function clarificationPayloadToWizardState(
  payload: ClarificationPayload
): ClarificationWizardState {
  let intro = typeof payload.intro === 'string' ? payload.intro.trim() : '';
  const questions: ParsedQuestion[] = payload.questions.map((q, i) => {
    const input = q.input;
    if (input.kind === 'freeform') {
      return {
        index: i + 1,
        questionId: q.id,
        prompt: q.prompt,
        choices: [],
      };
    }
    return {
      index: i + 1,
      questionId: q.id,
      prompt: q.prompt,
      choices: input.options.map((o) => ({key: o.id, label: o.label})),
    };
  });

  const first = questions[0];
  if (first) {
    let prompt = first.prompt;
    const embedded = splitEmbeddedPreambleBeforeFirstNumberedLine(prompt);
    if (embedded) {
      if (!intro) {
        intro = embedded.preamble;
      }
      prompt = embedded.question;
    }
    if (intro) {
      prompt = stripLeadingIntroFromPrompt(intro, prompt);
    }
    const trimmed = prompt.trim();
    questions[0] = {...first, prompt: trimmed || first.prompt.trim()};
  }

  return {intro, questions};
}

/** Allow only http(s) URLs for rendered source links (mitigate javascript: / data:). */
export function isAllowedHttpUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Seed chat context attachments from workstream links (https only, deduped). */
export function contextAttachmentsFromWorkstreamLinks(
  links: Array<{url: string; text: string}> | null | undefined,
  maxCount = MAX_WORKSTREAM_CONTEXT_ATTACHMENTS
): Array<{url: string; title: string}> {
  const raw = links ?? [];
  const out: Array<{url: string; title: string}> = [];
  const seen = new Set<string>();
  for (const link of raw) {
    const url = (link.url ?? '').trim();
    if (!url || !isAllowedHttpUrl(url)) continue;
    if (seen.has(url)) continue;
    seen.add(url);
    const title = (link.text ?? '').trim() || url;
    out.push({url, title});
    if (out.length >= maxCount) break;
  }
  return out;
}

export function simpleDraftHash(markdown: string): string {
  let h = 0;
  for (let i = 0; i < markdown.length; i++) {
    h = (h * 31 + markdown.charCodeAt(i)) | 0;
  }
  return `${markdown.length}:${h}`;
}

/** Last assistant text from raw Context Engine `get_response` body (same as admin preview). */
export function getAssistantTextFromResponse(
  response: WorkstreamContextEngineChatResponse
): string {
  return lastAssistantTextFromCeResponse(response as unknown as Record<string, unknown>);
}

const COMPANY_OS_CLARIFICATION_HEADER = '### COMPANY_OS_CLARIFICATION';

const CLARIFICATION_NUM_BLOCK = /^(\d+)\.\s+/gm;
const CLARIFICATION_CHOICE_LINE = /^([A-Z])\.\s+(.+)$/;
const CLARIFICATION_FIRST_QUESTION = /^1\.\s+/m;

function splitClarificationNumberedBlocks(body: string): Array<[string, string]> {
  const matches = [...body.matchAll(CLARIFICATION_NUM_BLOCK)];
  if (matches.length === 0) {
    return [];
  }
  const out: Array<[string, string]> = [];
  for (let i = 0; i < matches.length; i += 1) {
    const mm = matches[i];
    const num = mm[1];
    const startContent = (mm.index ?? 0) + mm[0].length;
    const endContent = i + 1 < matches.length ? (matches[i + 1].index ?? body.length) : body.length;
    const chunk = body.slice(startContent, endContent).trim();
    out.push([num, chunk]);
  }
  return out;
}

/**
 * Parse assistant text that uses `### COMPANY_OS_CLARIFICATION` wire format
 * (mirrors server `try_parse_clarification_payload`).
 */
export function tryParseClarificationPayloadFromAssistantText(
  text: string
): ClarificationPayload | null {
  const work = text.trim();
  if (!work || !work.includes(COMPANY_OS_CLARIFICATION_HEADER)) {
    return null;
  }
  const hi = work.indexOf(COMPANY_OS_CLARIFICATION_HEADER);
  const rest = work.slice(hi + COMPANY_OS_CLARIFICATION_HEADER.length).replace(/^\s+/, '');
  const m0 = CLARIFICATION_FIRST_QUESTION.exec(rest);
  if (!m0 || m0.index === undefined) {
    return null;
  }
  const intro = rest.slice(0, m0.index).trim();
  const body = rest.slice(m0.index);
  const blocks = splitClarificationNumberedBlocks(body);
  if (blocks.length === 0) {
    return null;
  }
  const questions: ClarificationPayload['questions'] = [];
  for (const [num, chunk] of blocks) {
    const qid = `q${num}`;
    const promptLines: string[] = [];
    const options: Array<{id: string; label: string}> = [];
    for (const ln of chunk.split('\n')) {
      const cm = ln.trim().match(CLARIFICATION_CHOICE_LINE);
      if (cm) {
        options.push({id: cm[1], label: cm[2].trim()});
      } else if (options.length === 0) {
        promptLines.push(ln);
      }
    }
    const prompt = promptLines.join('\n').trim();
    if (!prompt) {
      continue;
    }
    if (options.length >= 2) {
      questions.push({
        id: qid,
        prompt,
        input: {kind: 'single_select', options},
      });
    } else {
      questions.push({
        id: qid,
        prompt,
        input: {kind: 'freeform'},
      });
    }
  }
  if (questions.length === 0) {
    return null;
  }
  return {
    schema_version: '1',
    ...(intro ? {intro} : {}),
    questions,
  };
}

// Keep in sync with the CompanyOS CE status allowlist:
// ~/src/server/dropbox/context_engine/conversations/internal/handlers/graph_handlers/companyos/companyos_workstream_update_handler.py
const VALID_DRAFT_STATUSES = new Set([
  'on_track',
  'at_risk',
  'off_track',
  'complete',
  'cancelled',
  'missed',
  'archived',
  'no_status',
]);

function recordFromUnknown(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function stringFromRecord(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value.trim() : '';
}

function draftRecordFromCeResponse(raw: Record<string, unknown>): Record<string, unknown> | null {
  return recordFromUnknown(raw.draft);
}

function ceDraftRecordWithContent(
  payload: Record<string, unknown>
): Record<string, unknown> | null {
  const draft = draftRecordFromCeResponse(payload);
  if (!draft) {
    return null;
  }
  return stringFromRecord(draft, 'content') ? draft : null;
}

/** Preserve ``draft`` when later streamed CE frames omit it (matches server merge). */
export function mergeCeStreamingAssistantPayload(
  previous: Record<string, unknown> | null,
  current: Record<string, unknown>
): Record<string, unknown> {
  if (!previous) {
    return current;
  }
  const prevDraft = ceDraftRecordWithContent(previous);
  const currDraft = ceDraftRecordWithContent(current);
  if (prevDraft && !currDraft) {
    return {...current, draft: prevDraft};
  }
  return current;
}

function parseWriteAgentPayload(rawPayload: unknown): {
  status?: string;
} {
  if (typeof rawPayload !== 'string' || !rawPayload.trim()) {
    return {};
  }

  let payload: Record<string, unknown> | null = null;
  try {
    payload = recordFromUnknown(JSON.parse(rawPayload));
  } catch {
    return {};
  }

  const status = payload ? stringFromRecord(payload, 'status') : '';
  return {
    ...(status && VALID_DRAFT_STATUSES.has(status) ? {status} : {}),
  };
}

export function tryParseStructuredDraftFromCeResponse(
  response: WorkstreamContextEngineChatResponse
): {
  status?: string;
  body: string;
} | null {
  const raw = response as unknown as Record<string, unknown>;
  const draft = draftRecordFromCeResponse(raw);
  if (!draft) {
    return null;
  }
  const body = stringFromRecord(draft, 'content');
  if (!body) {
    return null;
  }
  const metadata = parseWriteAgentPayload(draft.write_agent_payload);
  return {
    ...metadata,
    body,
  };
}

export interface ContextEngineSource {
  title: string;
  url?: string | null;
  index?: string | null;
}

export interface ContextEngineStructuredResponse {
  status?: string;
  comment: string;
  is_draft_suggestion?: boolean;
  sources?: ContextEngineSource[];
  clarification?: ClarificationPayload;
  suggested_queries?: string[];
}

function parseSuggestedQueriesFromCeResponse(raw: Record<string, unknown>): string[] | undefined {
  const value = raw.suggested_queries;
  if (!Array.isArray(value)) {
    return undefined;
  }

  const queries: string[] = [];
  for (const item of value) {
    const query = recordFromUnknown(item)
      ? stringFromRecord(item as Record<string, unknown>, 'text')
      : '';
    if (query) {
      queries.push(query);
    }
    if (queries.length >= 3) {
      break;
    }
  }

  return queries.length > 0 ? queries : undefined;
}

/**
 * Context Engine sometimes separates prose blocks with a single ``\\n``. Markdown and
 * RichTextArea treat paragraph breaks as ``\\n\\n`` — expand lone newlines only.
 */
export function expandSingleNewlinesToParagraphBreaks(text: string): string {
  if (!text) {
    return text;
  }
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return normalized.replace(/(?<!\n)\n(?!\n)/g, '\n\n');
}

export function getStructuredResponseFromChat(
  response: WorkstreamContextEngineChatResponse
): ContextEngineStructuredResponse {
  const raw = response as unknown as Record<string, unknown>;
  const lastAssistant = lastAssistantTextFromCeResponse(raw);
  const suggestedQueries = parseSuggestedQueriesFromCeResponse(raw);
  const sourceRows = extractCeSourcesFromRaw(raw);
  const sources: ContextEngineSource[] | undefined =
    sourceRows.length > 0
      ? sourceRows.map((s) => ({
          title: s.title,
          url: s.url || null,
          index: s.index,
        }))
      : undefined;
  const responseExtras = {
    ...(sources && {sources}),
    ...(suggestedQueries && {suggested_queries: suggestedQueries}),
  };

  const clarification = tryParseClarificationPayloadFromAssistantText(lastAssistant);
  if (clarification?.questions?.length) {
    return {
      comment: '',
      clarification,
      is_draft_suggestion: false,
      ...responseExtras,
    };
  }

  const structuredDraft = tryParseStructuredDraftFromCeResponse(response);
  if (structuredDraft?.body?.trim()) {
    return {
      ...(structuredDraft.status !== undefined && {status: structuredDraft.status}),
      comment: expandSingleNewlinesToParagraphBreaks(structuredDraft.body.trim()),
      is_draft_suggestion: true,
      ...responseExtras,
    };
  }

  return {
    comment: expandSingleNewlinesToParagraphBreaks(lastAssistant.trim()),
    is_draft_suggestion: false,
    ...responseExtras,
  };
}

/** Symmetric with assistant `### COMPANY_OS_CLARIFICATION` — numbered answer lines for refine phase. */
export const COMPANY_OS_CLARIFICATION_ANSWERS_HEADER = '### COMPANY_OS_CLARIFICATION_ANSWERS';

export type ClarificationAnswerLine = {
  /** Same index as the assistant's "1.", "2." (see ParsedQuestion.index). */
  index: number;
  /** Text after "N. " — e.g. "(A) Label" or freeform answer. */
  payload: string;
};

/** Rich display for user bubbles (wire message may still use the markdown header + numbered lines). */
export type ClarificationAnswersDisplay = {
  intro?: string;
  pairs: Array<{
    /** Assistant question prompt (markdown). */
    prompt: string;
    /** Submitted answer text (markdown allowed). */
    answer: string;
    /** Same index as assistant numbering (1, 2, …). */
    questionIndex: number;
  }>;
  optionalNote?: string;
};

export type ParsedClarificationUserMessage = {
  intro?: string;
  answerLines: ClarificationAnswerLine[];
  optionalNote?: string;
};

/**
 * Parse a user message that contains `COMPANY_OS_CLARIFICATION_ANSWERS_HEADER`.
 * Returns null if the marker is absent (normal freeform user message).
 */
export function parseClarificationUserMessage(text: string): ParsedClarificationUserMessage | null {
  const header = COMPANY_OS_CLARIFICATION_ANSWERS_HEADER;
  const at = text.indexOf(header);
  if (at === -1) return null;
  const before = text.slice(0, at).replace(/\s+$/u, '');
  const after = text.slice(at + header.length).replace(/^\s+/u, '');
  const intro = before.trim() ? before.trim() : undefined;

  const lines = after.split(/\r?\n/u);
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') {
    i += 1;
  }
  const answerLines: ClarificationAnswerLine[] = [];
  const lineRe = /^(\d+)\.\s+(.*)$/u;
  while (i < lines.length) {
    const raw = lines[i];
    const m = raw.match(lineRe);
    if (!m) break;
    const idx = parseInt(m[1], 10);
    if (!Number.isFinite(idx)) break;
    answerLines.push({index: idx, payload: m[2]});
    i += 1;
  }
  while (i < lines.length && lines[i].trim() === '') {
    i += 1;
  }
  const rest = i < lines.length ? lines.slice(i).join('\n').trim() : '';
  const optionalNote = rest ? rest : undefined;

  if (answerLines.length === 0) return null;
  return {intro, answerLines, optionalNote};
}

/** Fallback display when only the wire string is available (no question prompts). */
export function clarificationAnswersDisplayFromParsed(
  parsed: ParsedClarificationUserMessage
): ClarificationAnswersDisplay {
  return {
    ...(parsed.intro !== undefined && {intro: parsed.intro}),
    pairs: parsed.answerLines.map((a) => ({
      prompt: '',
      answer: a.payload.trim(),
      questionIndex: a.index,
    })),
    ...(parsed.optionalNote !== undefined && {optionalNote: parsed.optionalNote}),
  };
}

export function formatClarificationUserMessage(parts: {
  intro?: string;
  answers: ClarificationAnswerLine[];
  optionalNote?: string;
}): string {
  const chunks: string[] = [];
  if (parts.intro?.trim()) {
    chunks.push(parts.intro.trim());
  }
  const answered = parts.answers.filter((a) => a.payload.trim());
  if (answered.length > 0) {
    if (chunks.length > 0) {
      chunks.push('');
    }
    chunks.push(COMPANY_OS_CLARIFICATION_ANSWERS_HEADER);
    chunks.push('');
    for (const a of answered) {
      chunks.push(`${a.index}. ${a.payload.trim()}`);
    }
  }
  if (parts.optionalNote?.trim()) {
    if (chunks.length > 0) {
      chunks.push('');
    }
    chunks.push(parts.optionalNote.trim());
  }
  return chunks.join('\n');
}
